import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MeetingPublic } from '@repo/types/meetings';
import type { Dispatch, SetStateAction } from 'react';

import { useEditMeeting } from '@repo/api/meeting/useEditMeeting';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { format } from 'date-fns';
import { CalendarIcon, DotIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface CancelMeetingProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  event?: MeetingPublic;
  queryParams?: EventCalendarRangeDates;
}

export default function CancelMeeting(cancelMeetingProps: CancelMeetingProps) {
  const { open, onOpenChange, event, queryParams } = cancelMeetingProps;

  const isCancelled: boolean = event?.isCancelled || false;

  const { mutateAsync, reset } = useEditMeeting(queryParams);

  const onClose = () => {
    onOpenChange(false);
    reset();
  };

  const onSubmit = async () => {
    if (!event) {
      return;
    }
    mutateAsync({
      id: event.id,
      isCancelled: !event.isCancelled,
    }).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <CalendarIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{isCancelled ? 'Przywrócić' : 'Odwołać'} wybrane spotkanie?</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>
                {isCancelled ? 'Jesteś pewien czy chcesz przywrócić spotkanie?' : 'Jesteś pewien czy chcesz odwołać spotkanie?'}
              </AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto px-4'}>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-14'}>Nazwa: </span>
              <p className="text-sm">{event?.name}</p>
            </div>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-14'}>Data: </span>
              <p className={'flex items-center text-sm'}>
                {event?.startDate ? format(new Date(event.startDate), 'dd MMM yyyy, HH:mm') : ''}
                <DotIcon className={'size-4'} />
                {event?.endDate ? format(new Date(event.endDate), 'dd MMM yyyy, HH:mm') : ''}
              </p>
            </div>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-14'}>Typ:</span>
              <Badge variant={'orange'}>{event?.type === 'remote' ? 'Zdalnie' : 'Stacjonarnie'}</Badge>
            </div>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
